import type { ChargingState, Robot, RobotAvailability, RobotHealth } from "../../robots/types";

// This determines how often the simulation loop should run, but the actual
// change in the states are determined by the timestamps in the RobotSimulationState
export const SIMULATION_TICK_INTERVAL_MS = 1000;

const BATTERY_DRAIN_PER_SECOND = 100 / 1800; // 100 percent per 30 minutes
const BATTERY_CHARGE_PER_SECOND = 100 / 600; // 100 percent per 10 minutes
const BATTERY_LOW_THRESHOLD = 15;
const BATTERY_FULL = 100;

const AVAILABILITY_UPDATE_INTERVAL_MS = 32000;
const AVAILABILITY_POOL: RobotAvailability[] = (
    [
        { value: "available", weight: 5 },
        { value: "blocked", weight: 2 },
        { value: "busy", weight: 10 },
        { value: "paused", weight: 1 },
    ] satisfies { value: RobotAvailability; weight: number }[]
).flatMap(({ value, weight }) => Array.from({ length: weight }, () => value));

const HEALTH_UPDATE_INTERVAL_MS = 12000;
const HEALTH_POOL: RobotHealth[] = (
    [
        { value: "healthy", weight: 25 },
        { value: "warning", weight: 3 },
        { value: "critical", weight: 1 },
    ] satisfies { value: RobotHealth; weight: number }[]
).flatMap(({ value, weight }) => Array.from({ length: weight }, () => value));

const CONNECTIVITY_ONLINE_MIN_MS = 30000;
const CONNECTIVITY_ONLINE_MAX_MS = 180000;
const CONNECTIVITY_OFFLINE_MIN_MS = 3000;
const CONNECTIVITY_OFFLINE_MAX_MS = 18000;

interface RobotSimulationState {
    battery: number;
    charging: ChargingState;
    chargingUp: boolean;
    health: RobotHealth;
    availability: RobotAvailability;
    alarms: number;
    online: boolean;
    lastSeen: number;
    lastBatteryUpdateAt: number;
    nextAvailabilityChangeAt: number;
    nextConnectivityChangeAt: number;
    nextHealthChangeAt: number;
}

function randomBetween(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function randomItem<T>(items: T[]): T {
    return items[Math.floor(Math.random() * items.length)];
}

const simulationState = new Map<string, RobotSimulationState>();

export function tickRobotSimulation(robot: Robot, now: number): Partial<Robot> {
    let state = simulationState.get(robot.id);
    if (!state) {
        const online = robot.connectivity !== "offline";
        state = {
            battery: robot.battery ?? Math.random() * 100,
            charging: robot.charging ?? "discharging",
            chargingUp: false,
            health: robot.health,
            availability: robot.availability ?? "available",
            alarms: robot.alarms,
            online,
            lastSeen: robot.lastSeen ? new Date(robot.lastSeen).getTime() : now,
            lastBatteryUpdateAt: now,
            nextAvailabilityChangeAt: now + AVAILABILITY_UPDATE_INTERVAL_MS,
            nextConnectivityChangeAt:
                now +
                (online
                    ? randomBetween(CONNECTIVITY_ONLINE_MIN_MS, CONNECTIVITY_ONLINE_MAX_MS)
                    : randomBetween(CONNECTIVITY_OFFLINE_MIN_MS, CONNECTIVITY_OFFLINE_MAX_MS)),
            nextHealthChangeAt: now + HEALTH_UPDATE_INTERVAL_MS,
        };
        simulationState.set(robot.id, state);
    }

    // Battery drain/charge cycle, advanced continuously from elapsed time.
    const batterySeconds = (now - state.lastBatteryUpdateAt) / 1000;
    state.lastBatteryUpdateAt = now;
    if (state.chargingUp) {
        state.battery = Math.min(
            BATTERY_FULL,
            state.battery + BATTERY_CHARGE_PER_SECOND * batterySeconds,
        );
        if (state.battery >= BATTERY_FULL) {
            state.chargingUp = false;
            state.charging = "full";
            state.availability = "available";
        } else {
            state.charging = "charging";
        }
    } else {
        state.battery = Math.max(0, state.battery - BATTERY_DRAIN_PER_SECOND * batterySeconds);
        if (state.battery <= BATTERY_LOW_THRESHOLD) {
            state.chargingUp = true;
            state.charging = "charging";
            state.availability = "charging";
        } else {
            state.charging = "discharging";
        }
    }

    // Connectivity flapping: online for a while, then a short dropout.
    if (now >= state.nextConnectivityChangeAt) {
        state.online = !state.online;
        state.nextConnectivityChangeAt =
            now +
            (state.online
                ? randomBetween(CONNECTIVITY_ONLINE_MIN_MS, CONNECTIVITY_ONLINE_MAX_MS)
                : randomBetween(CONNECTIVITY_OFFLINE_MIN_MS, CONNECTIVITY_OFFLINE_MAX_MS));
    }

    // Health drifts, weighted heavily towards healthy.
    if (now >= state.nextHealthChangeAt) {
        state.nextHealthChangeAt = now + HEALTH_UPDATE_INTERVAL_MS;
        state.health = randomItem(HEALTH_POOL);
    }

    // Availability wanders while the robot is running (not while charging, which
    // owns the availability during that phase).
    if (now >= state.nextAvailabilityChangeAt) {
        state.nextAvailabilityChangeAt = now + AVAILABILITY_UPDATE_INTERVAL_MS;
        if (!state.chargingUp) {
            state.availability = randomItem(AVAILABILITY_POOL);
        }
    }

    // Only a connected robot updates its last-seen timestamp.
    if (state.online) {
        state.lastSeen = now;
    }

    // Sum up alarms
    state.alarms = 0;
    if (state.availability === "blocked") {
        state.alarms += 1;
    }
    if (state.health === "critical") {
        state.alarms += 1;
    }

    // Offline robots don't receive updates
    if (!state.online) {
        return {
            connectivity: "offline",
        };
    }

    return {
        connectivity: "online",
        battery: Math.round(state.battery),
        charging: state.charging,
        health: state.health,
        availability: state.availability,
        alarms: state.alarms,
        lastSeen: new Date(state.lastSeen),
    };
}
